<script setup lang="ts">
import {
  useBillingStore,
  type BillingTermKind,
  type SimulationQuote,
} from '~/store/billing'
import type { CheckoutTrialSummary } from '~/types/checkout-trial'

const props = defineProps<{
  trial?: CheckoutTrialSummary | null;
  quote: SimulationQuote | null;
  simulating?: boolean;
  simulationError?: string | null;
  isNewSubscription: boolean;
  /**
   * Existing-subscription orders charge the PRORATA: the hours left in the
   * current cycle at the quoted rate, estimated client-side (µ¢ string, null =
   * unknown/not applicable). The instance then renews with the subscription
   * at the full period price. Nothing is prepaid beyond the cycle.
   */
  prorataEstimate?: string | null
  /** Hours the prorata estimate covers. */
  prorataHours?: number | null
  /**
   * Term being bought. Enables the per-month headline — the unit the plan
   * table and the cycle picker quote — so the summary agrees with them instead
   * of leading with a cycle total whose meaning changes per term.
   * Optional: callers that omit it keep the plain hourly/cycle breakdown.
   */
  term?: BillingTermKind
  /** µ¢/month this configuration would cost with no commitment, when cheaper
   * terms make that a real saving. Null/omitted hides the comparison. */
  onDemandMonthlyMicroCents?: string | null
  /** New on-demand order: the prepaid credit to invoice, independent of usage. */
  initialCreditCents?: string | null
}>()

const billing = useBillingStore()
const pricing = useCheckoutPricing()
const labels = useBillingLabels()

/**
 * The priced lines, each with the label this console writes for it (the API's
 * own is pt-BR — see `simulationLineLabel`). Extra disks are numbered by their
 * position among the other extra disks, which is the only thing that tells two
 * identically-sized ones apart.
 */
const rows = computed(() => {
  let extraDiskIndex = 0
  return (props.quote?.line_items ?? [])
    .filter((li) => !li.rejected)
    .map((li) => ({
      li,
      label: labels.simulationLineLabel(
        li,
        li.kind === 'extra_disk' ? extraDiskIndex++ : 0
      ),
    }))
})

/** What this configuration costs per month, normalised across every term. */
const monthlyMicroCents = computed(() =>
  pricing.perMonth(props.quote?.total_hourly_rate_micro_cents)
)

const savingsPerMonth = computed(() => {
  if (!props.onDemandMonthlyMicroCents || monthlyMicroCents.value == null) {
    return null
  }
  try {
    const delta =
      BigInt(props.onDemandMonthlyMicroCents) - monthlyMicroCents.value
    return delta > 0n ? delta : null
  } catch {
    return null
  }
})

const isOnDemand = computed(() => props.term === 'ON_DEMAND')

const hidesRedundantCycleTotal = computed(
  () =>
    !!props.term &&
    monthlyMicroCents.value != null &&
    pricing.commitmentMonths(props.term) === 1
)
</script>

<template>
  <div v-if="simulating" class="text-xs text-muted">
    {{ $t('billing.page.checkout.summary.calculating') }}
  </div>
  <div v-else-if="simulationError" class="text-xs text-error">
    {{ simulationError }}
  </div>
  <CheckoutTrialSummary v-else-if="quote && trial" v-bind="trial" />
  <div v-else-if="quote" class="space-y-1">
    <div
      v-for="(row, i) in rows"
      :key="i"
      class="flex justify-between text-xs"
    >
      <span class="text-muted truncate pr-2"
        >{{ row.label
        }}<span v-if="row.li.free_quantity_applied" class="text-emerald-600">
          {{ $t('billing.page.checkout.summary.freeQty', { qty: row.li.free_quantity_applied }) }}</span
        ></span
      >
      <span class="font-mono whitespace-nowrap">{{
        billing.formatMicroCents(
          row.li.cycle_subtotal_micro_cents || row.li.hourly_subtotal_micro_cents
        )
      }}<span v-if="isOnDemand">/h</span></span>
    </div>
    <USeparator />

    <!-- Headline in the unit everything else on the page quotes.

         A polite live region, because "your price updates as you configure" is
         this page's entire promise and it was otherwise a purely visual event:
         a customer using a screen reader changed the plan, the disk or an
         add-on and was told nothing at all. `aria-atomic` so the label and the
         figure are read as one sentence rather than a bare number.

         Announcing the SETTLED figure is what makes this usable: the quote is
         already debounced 250 ms behind the controls, so a slider drag produces
         one announcement rather than one per pixel. -->
    <div
      v-if="term && monthlyMicroCents != null"
      class="pt-1"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="flex items-baseline justify-between gap-2">
        <span class="text-sm font-medium">
          {{
            isOnDemand
              ? $t('billing.page.checkout.summary.estimatedPerMonth')
              : $t('billing.page.checkout.summary.perMonth')
          }}
        </span>
        <!-- Marked alongside the cycle total: this is the figure that is
             ALWAYS on screen (the cycle line is hidden for a one-month
             commitment, where per-month says the same thing), so it is what a
             test can watch across a change of configuration. -->
        <span data-testid="totals-per-month" class="text-xl font-bold tabular-nums">
          {{ pricing.fmt(monthlyMicroCents) }}
        </span>
      </div>
      <div
        v-if="savingsPerMonth"
        class="flex items-center justify-end gap-1 text-xs text-success font-medium"
      >
        <UIcon name="i-lucide-piggy-bank" class="size-3.5" />
        {{
          $t('billing.page.checkout.summary.savingVsOnDemand', {
            amount: pricing.fmt(savingsPerMonth),
          })
        }}
      </div>
    </div>

    <div class="flex justify-between text-xs pt-1">
      <span class="text-muted">{{ $t('billing.page.checkout.summary.totalPerHour') }}</span>
      <span class="font-mono"
        >{{ billing.formatMicroCents(quote.total_hourly_rate_micro_cents) }}/h</span
      >
    </div>
    <!-- On a one-month cycle the "cycle total" IS the monthly headline above,
         so printing it twice just makes the summary look longer than it is. -->
    <div
      v-if="quote.cycle_hours && !hidesRedundantCycleTotal"
      class="flex justify-between text-sm font-semibold"
    >
      <span>{{
        isNewSubscription
          ? $t('billing.page.checkout.summary.totalCycle')
          : $t('billing.page.checkout.summary.renewsAtFullPeriod')
      }}</span>
      <!-- Marked because two suites assert that the invoice equals the figure
           the customer was shown, and this is that figure. Every other money
           value on the page is a rate, a saving or a line item. -->
      <span data-testid="totals-cycle-total">{{
        billing.formatMicroCents(quote.total_cycle_micro_cents)
      }}</span>
    </div>
    <!-- Only a genuinely uncommitted quote gets this line. It must NOT ride on
         the branch above, which also hides for a one-month commitment. -->
    <div v-else-if="!quote.cycle_hours" class="text-xs text-muted pt-1">
      {{ $t('billing.page.checkout.summary.noCommitment') }}
    </div>
    <template v-if="isOnDemand && isNewSubscription && initialCreditCents">
      <USeparator class="my-2" />
      <div class="flex justify-between gap-2 text-sm font-semibold" role="status" aria-live="polite">
        <span>{{ $t('billing.page.checkout.recharge.dueNow') }}</span>
        <span data-testid="totals-initial-credit" class="font-mono">{{ billing.formatCents(initialCreditCents) }}</span>
      </div>
      <p class="text-xs text-muted pt-1">
        {{ $t('billing.page.checkout.recharge.description') }}
      </p>
    </template>
    <!-- Existing subscription only: what is charged NOW is the prorata to
         the end of the current cycle; the full period above is what the
         instance renews at afterwards. -->
    <template v-if="!isNewSubscription && quote.cycle_hours && prorataEstimate">
      <div class="flex justify-between text-sm font-semibold pt-1">
        <span>{{ $t('billing.page.checkout.summary.chargeToCycleEnd', { hours: prorataHours ?? 0 }) }}</span>
        <span data-testid="totals-prorata" class="font-mono">{{ billing.formatMicroCents(prorataEstimate) }}</span>
      </div>
      <p class="text-[11px] text-muted leading-snug pt-1">
        {{ $t('billing.page.checkout.summary.prorataExplainer') }}
      </p>
    </template>
  </div>
</template>
